import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, Building2, Scale } from 'lucide-react';
import { Button } from '../ui/button';

const DivisionsSection = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const goldGradient = 'linear-gradient(135deg, #B58C5A, #A27849, #8C653A)';

  const divisions = [
    {
      icon: Scale,
      titleAr: 'التقاضي',
      titleEn: 'Litigation',
      descriptionAr: 'تمثيل قانوني احترافي أمام الجهات القضائية مع متابعة دقيقة لمراحل القضية.',
      descriptionEn: 'Professional legal representation before judicial authorities with close follow-up at every stage.',
      link: '/divisions/litigation'
    },
    {
      icon: Building2,
      titleAr: 'العقود والشركات',
      titleEn: 'Contracts & Corporate',
      descriptionAr: 'صياغة العقود وخدمات الشركات بما ينظم الالتزامات ويحمي المصالح بوضوح.',
      descriptionEn: 'Contract drafting and corporate services that clarify obligations and protect interests.',
      link: '/divisions/contracts-and-corporate'
    },
    {
      icon: Briefcase,
      titleAr: 'الدراسات والاستشارات',
      titleEn: 'Studies & Consultations',
      descriptionAr: 'دراسات قانونية واستشارات متخصصة تدعم القرار وتبني المسار النظامي المناسب.',
      descriptionEn: 'Specialized legal studies and consultations that support sound decisions and proper legal direction.',
      link: '/divisions/consultations'
    }
  ];

  return (
    <section id="divisions" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-wazin-navy">
            {i18n.language === 'ar' ? 'مجالات خبرتنا القانونية' : 'Our Areas of Legal Expertise'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {i18n.language === 'ar'
              ? 'نركّز على أبرز المجالات القانونية التي تمثل احتياجاً متكرراً للأفراد وقطاع الأعمال، ضمن معالجة مهنية واضحة ومتكاملة.'
              : 'We focus on key legal areas most relevant to individuals and businesses, delivered through clear and professional legal handling.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {divisions.map((division, index) => {
            const Icon = division.icon;

            return (
              <div
                key={index}
                className="group bg-white p-8 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 overflow-hidden hover:-translate-y-1 hover:border-[#A27849]/60"
                style={{ animationDelay: `${index * 0.1}s` }}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-105 transition-transform duration-300 shadow-lg"
                  style={{ background: goldGradient }}
                >
                  <Icon className="w-9 h-9 text-white" />
                </div>

                <h3 className="text-2xl font-bold mb-4 text-wazin-navy text-center group-hover:text-[#8C653A] transition-colors duration-300">
                  {i18n.language === 'ar' ? division.titleAr : division.titleEn}
                </h3>

                <p className="text-gray-600 text-center leading-8 text-base min-h-[96px] mb-8">
                  {i18n.language === 'ar' ? division.descriptionAr : division.descriptionEn}
                </p>

                <div className="text-center">
                  <Button
                    onClick={() => navigate(division.link)}
                    className="px-6 py-5 text-base font-bold text-white rounded-xl gap-2 hover:opacity-95 transition-all duration-300 shadow-md hover:shadow-lg"
                    style={{ background: goldGradient }}
                  >
                    {i18n.language === 'ar' ? 'اعرف المزيد' : 'Learn More'}
                    <ArrowRight className={`w-5 h-5 ${i18n.language === 'ar' ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Button
            onClick={() => navigate('/divisions')}
            className="text-white px-10 py-6 text-lg hover:shadow-2xl transition-all duration-300 gap-3 font-bold rounded-xl hover:scale-105"
            style={{ background: goldGradient }}
          >
            {i18n.language === 'ar' ? 'عرض جميع الدوائر' : 'View All Divisions'}
            <ArrowRight className={`w-5 h-5 ${i18n.language === 'ar' ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default DivisionsSection;
